import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogType,
    PrimaryButton,
    DefaultButton,
    IconButton,
    Stack,
    TextField,
    Dropdown,
    IDropdownOption,
    ShimmeredDetailsList,
    SelectionMode,
    IColumn,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    TagPicker,
    NormalPeoplePicker,
    DatePicker,
    ComboBox,
    Icon
} from '@fluentui/react';
import Text from '../../../../../typography/text';
import Label from '../../../../../typography/label';
import FileService from '../../../../../../services/general/file';
import OpenAIService from '../../../../../../services/systems/openai';
import GeneralService from '../../../../../../services/general';
import ErrorService from '../../../../../../services/general/error';
import CompanyService from '../../../../../../services/users/company';
import WarehouseService from '../../../../../../services/warehouses';
import QuantityUnitService from '../../../../../../services/general/quantityUnit';
import WeightTypes from '../../../../../../manifests/weightTypes';
import { IGoodsResourceShortProps } from '../../../../../../props/goods/goods';
import { ICompanyResourceShort } from '../../../../../../props/users/company';
import { IUserResourceShortProps } from '../../../../../../props/users/user';
import { IWarehouseResourceShortProps } from '../../../../../../props/warehouses/warehouse';
import { IQuantityUnitResourceShortProps } from '../../../../../../props/general/quantityUnit';
import moment from 'moment';

export interface ExtractedTallyItem {
    id: string;
    itemNumber: number;
    jenisBarang: string;
    quantityAmount: string;
    quantityUnit: string;
    weightAmount: string;
    weightUnit: string;
    length?: string;
    width?: string;
    height?: string;
    dimensionUnit?: 'cm' | 'm' | 'mm';
    company?: ICompanyResourceShort;
    customer?: IUserResourceShortProps;
    warehouse?: IWarehouseResourceShortProps;
    locationDetails?: string;
    supplier?: string;
    deliveryDate?: string;
    date?: string;
    notes?: string;
    matchedGoods?: IGoodsResourceShortProps;
    matchingStatus?: 'matched' | 'new' | 'searching';
}

interface TallySheetScanModalProps {
    isOpen: boolean;
    onDismiss(): void;
    onApply(items: ExtractedTallyItem[], containerInfo?: any): void;
    defaultCompany?: ICompanyResourceShort;
    defaultCustomer?: IUserResourceShortProps;
    defaultWarehouse?: IWarehouseResourceShortProps;
}

const MODEL_OPTIONS: IDropdownOption[] = [
    { key: 'google-ai-studio', text: 'Google AI Studio Gemini Flash (Free Tier - 100% Gratis - Recommended)' },
    { key: 'google/gemini-2.5-flash', text: 'OpenRouter Google Gemini 2.5 Flash' },
    { key: 'openai/gpt-4o-mini', text: 'OpenAI GPT-4o Mini (Vision OCR)' },
];

const DIMENSION_UNITS = [
    { key: 'cm', text: 'cm' },
    { key: 'm', text: 'm' },
    { key: 'mm', text: 'mm' }
];

const TallySheetScanModal: React.FC<TallySheetScanModalProps> = (props: TallySheetScanModalProps) => {
    const { isOpen, onDismiss, onApply, defaultCompany, defaultCustomer, defaultWarehouse } = props;

    const [file, setFile] = useState<File | undefined>();
    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [selectedModel, setSelectedModel] = useState<string>('google-ai-studio');
    const [extracting, setExtracting] = useState<boolean>(false);
    const [extractedItems, setExtractedItems] = useState<ExtractedTallyItem[]>([]);
    const [containerInfo, setContainerInfo] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [expandedItemId, setExpandedItemId] = useState<string | undefined>();

    const [quantityUnits, setQuantityUnits] = useState<IQuantityUnitResourceShortProps[]>([]);

    useEffect(() => {
        if (isOpen) {
            loadQuantityUnits();
        }
    }, [isOpen]);

    const loadQuantityUnits = async () => {
        try {
            const units = await QuantityUnitService.retrieve();
            setQuantityUnits(units);
        } catch (e) {
            console.error('Failed to load quantity units:', e);
        }
    };

    const handleFileChange = async (files: FileList | File[] | null) => {
        if (!files || files.length === 0) return;
        const selectedFile = files[0];
        setFile(selectedFile);
        setErrorMessage(undefined);
        setExtractedItems([]);
        setContainerInfo(null);
        setExpandedItemId(undefined);

        try {
            const b64List = await FileService.getBase64(selectedFile);
            setPreviewImages(b64List);
        } catch (e) {
            console.error(e);
            setErrorMessage("Gagal membaca preview gambar: " + ErrorService.getMessage(e));
        }
    };

    const handleProcessOCR = async () => {
        if (!file || previewImages.length === 0) return;

        setExtracting(true);
        setErrorMessage(undefined);
        setExtractedItems([]);
        setExpandedItemId(undefined);

        try {
            const fd = new FormData();
            fd.append('type', 'tallySheet');
            fd.append('model', selectedModel);
            previewImages.forEach((b64) => fd.append('base64[]', b64));

            const response = await OpenAIService.extractDocument(fd);

            let resData = response?.data || response;
            if (typeof resData === 'string') {
                try {
                    resData = JSON.parse(resData);
                } catch (e) {
                    console.error("JSON parse error:", e);
                }
            }

            const items: any[] = resData?.items || (Array.isArray(resData) ? resData : []);
            const parsedContainerInfo = resData?.containerInfo || null;
            setContainerInfo(parsedContainerInfo);

            if (!items || items.length === 0) {
                setErrorMessage("AI tidak menemukan baris barang dari foto Tally Sheet ini. Mohon pastikan foto cukup jelas.");
                setExtracting(false);
                return;
            }

            const formattedItems: ExtractedTallyItem[] = items.map((it: any, idx: number) => ({
                id: GeneralService.guid(),
                itemNumber: it.itemNumber || idx + 1,
                jenisBarang: it.jenisBarang || it.name || it.description || '',
                quantityAmount: it.quantityAmount ? String(it.quantityAmount) : (it.quantity ? String(it.quantity) : '1'),
                quantityUnit: it.quantityUnit || it.unit || 'CTN',
                weightAmount: it.weightAmount ? String(it.weightAmount) : '',
                weightUnit: it.weightUnit || 'kg',
                dimensionUnit: 'cm',
                company: defaultCompany,
                customer: defaultCustomer,
                warehouse: defaultWarehouse,
                date: it.date || '',
                notes: it.notes || '',
                matchingStatus: 'new'
            }));

            setExtractedItems(formattedItems);
            setExtracting(false);
        } catch (e: any) {
            console.error(e);
            let errMsg = ErrorService.getMessage(e);
            if (errMsg.includes("500") || errMsg.includes("Terjadi kesalahan") || errMsg.includes("Request failed")) {
                errMsg += " — Jika masalah berlanjut, coba ganti opsi 'Model AI OCR' di atas ke pilihan model AI lainnya (seperti OpenRouter / GPT-4o Mini).";
            }
            setErrorMessage("Gagal mengekstrak Tally Sheet via AI: " + errMsg);
            setExtracting(false);
        }
    };

    const handleItemChange = (id: string, field: keyof ExtractedTallyItem, value: any) => {
        setExtractedItems((prev) =>
            prev.map((item) => {
                if (item.id === id) {
                    return { ...item, [field]: value };
                }
                return item;
            })
        );
    };

    const handleRemoveRow = (id: string) => {
        if (expandedItemId === id) setExpandedItemId(undefined);
        setExtractedItems((prev) => prev.filter((it) => it.id !== id));
    };

    const handleAddBlankRow = () => {
        const newItemId = GeneralService.guid();
        setExtractedItems((prev) => [
            ...prev,
            {
                id: newItemId,
                itemNumber: prev.length + 1,
                jenisBarang: '',
                quantityAmount: '1',
                quantityUnit: 'CTN',
                weightAmount: '',
                weightUnit: 'kg',
                dimensionUnit: 'cm',
                company: defaultCompany,
                customer: defaultCustomer,
                warehouse: defaultWarehouse,
                matchingStatus: 'new'
            }
        ]);
        setExpandedItemId(newItemId);
    };

    const handleApply = () => {
        const validItems = extractedItems.filter((it) => it.jenisBarang && it.jenisBarang.trim() !== '');
        onApply(validItems, containerInfo);
        onDismiss();
    };

    // Picker search handlers
    const _onSearchCompanies = async (keyword: string) => {
        try {
            const qs: string[] = [];
            if (keyword && keyword.trim() !== '') { qs.push(`search=${encodeURIComponent(keyword.trim())}`); }
            const res = await CompanyService.retrieve(qs.join("&"));
            const list = res?.data || (Array.isArray(res) ? res : []);
            return list.map((item: any) => ({
                key: item.id,
                name: item.name,
                metadata: item
            }));
        } catch (error) {
            console.error("Search companies error:", error);
            return [];
        }
    };

    const _onSearchCustomers = async (companyId?: string, keyword: string = '') => {
        try {
            if (!companyId) return [];
            keyword = keyword.toLowerCase();
            const users = await CompanyService.getUsers(companyId);
            return users.filter((user: any) => {
                return (user.email || '').toLowerCase().indexOf(keyword) > -1 ||
                    (user.name || '').toLowerCase().indexOf(keyword) > -1 ||
                    (user.phoneNumber || "").toLowerCase().indexOf(keyword) > -1;
            }).map((user: any) => ({
                id: user.id,
                text: user.name,
                secondaryText: user.email,
                imageInitials: GeneralService.getInitial(user.name),
                metadata: user,
            }));
        } catch (error) {
            console.error("Search customers error:", error);
            return [];
        }
    };

    const _onSearchWarehouse = async (keyword: string) => {
        try {
            const qs: string[] = [];
            if (keyword && keyword.trim() !== '') { qs.push(`search=${encodeURIComponent(keyword.trim())}`); }
            const res = await WarehouseService.retrieve.get(qs.join("&"));
            const list = Array.isArray(res) ? res : ((res as any)?.data || []);
            return list.map((item: any) => ({
                key: item.id,
                name: item.name,
                metadata: item
            }));
        } catch (error) {
            console.error("Search warehouse error:", error);
            return [];
        }
    };

    const columns: IColumn[] = [
        {
            key: 'no',
            name: 'No.',
            minWidth: 30,
            maxWidth: 35,
            onRender: (item: ExtractedTallyItem, idx?: number) => (
                <Stack styles={{ root: { paddingTop: 6 } }}>
                    <Text size="small">{(idx || 0) + 1}</Text>
                </Stack>
            )
        },
        {
            key: 'jenisBarang',
            name: 'Nama / Jenis Barang',
            minWidth: 170,
            onRender: (item: ExtractedTallyItem) => {
                const hasDetails = item.company || item.customer || item.length || item.supplier || item.warehouse;
                return (
                    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { padding: '4px 0' } }}>
                        <TextField
                            value={item.jenisBarang}
                            onChange={(e, val) => handleItemChange(item.id, 'jenisBarang', val || '')}
                            disabled={extracting}
                            placeholder="Nama / Jenis Barang"
                        />
                        {hasDetails && (
                            <Stack horizontal wrap tokens={{ childrenGap: 6 }} verticalAlign="center">
                                {item.company && (
                                    <span style={{ fontSize: 10, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        🏢 {item.company.name}
                                    </span>
                                )}
                                {item.customer && (
                                    <span style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        👤 {item.customer.name}
                                    </span>
                                )}
                                {item.warehouse && (
                                    <span style={{ fontSize: 10, background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        🏭 {item.warehouse.name}
                                    </span>
                                )}
                                {(item.length || item.width || item.height) && (
                                    <span style={{ fontSize: 10, background: '#f1f5f9', color: '#334155', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        📐 {item.length || 0}x{item.width || 0}x{item.height || 0} {item.dimensionUnit || 'cm'} ({GeneralService.calculateVolume(item.length, item.width, item.height, item.dimensionUnit || 'cm')} m3)
                                    </span>
                                )}
                                {item.supplier && (
                                    <span style={{ fontSize: 10, background: '#f3e8ff', color: '#6b21a8', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                        🏷️ {item.supplier}
                                    </span>
                                )}
                            </Stack>
                        )}
                    </Stack>
                );
            }
        },
        {
            key: 'qty',
            name: 'Jumlah & Satuan',
            minWidth: 125,
            maxWidth: 140,
            onRender: (item: ExtractedTallyItem) => (
                <Stack horizontal tokens={{ childrenGap: 4 }} styles={{ root: { padding: '4px 0' } }}>
                    <TextField
                        value={item.quantityAmount}
                        styles={{ root: { width: 55 } }}
                        onChange={(e, val) => handleItemChange(item.id, 'quantityAmount', val || '')}
                        disabled={extracting}
                        placeholder="Jumlah"
                    />
                    <TextField
                        value={item.quantityUnit}
                        styles={{ root: { width: 60 } }}
                        onChange={(e, val) => handleItemChange(item.id, 'quantityUnit', val || '')}
                        disabled={extracting}
                        placeholder="Satuan"
                    />
                </Stack>
            )
        },
        {
            key: 'weight',
            name: 'Berat',
            minWidth: 85,
            maxWidth: 100,
            onRender: (item: ExtractedTallyItem) => (
                <Stack styles={{ root: { padding: '4px 0' } }}>
                    <TextField
                        value={item.weightAmount}
                        onChange={(e, val) => handleItemChange(item.id, 'weightAmount', val || '')}
                        disabled={extracting}
                        placeholder="kg"
                    />
                </Stack>
            )
        },
        {
            key: 'action',
            name: 'Create Goods Form',
            minWidth: 110,
            maxWidth: 120,
            onRender: (item: ExtractedTallyItem) => {
                const isExpanded = expandedItemId === item.id;
                return (
                    <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center" styles={{ root: { paddingTop: 4 } }}>
                        <DefaultButton
                            iconProps={{ iconName: isExpanded ? 'ChevronUp' : 'Edit' }}
                            text={isExpanded ? 'Tutup' : 'Form'}
                            title="Buka form detail Create Goods per barang"
                            styles={{
                                root: {
                                    minWidth: 65,
                                    height: 32,
                                    padding: '0 8px',
                                    backgroundColor: isExpanded ? '#0284c7' : '#f0f9ff',
                                    color: isExpanded ? '#ffffff' : '#0369a1',
                                    borderColor: '#bae6fd'
                                }
                            }}
                            onClick={() => setExpandedItemId(isExpanded ? undefined : item.id)}
                            disabled={extracting}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            title="Hapus baris"
                            styles={{ root: { width: 32, height: 32, color: '#ef4444' } }}
                            onClick={() => handleRemoveRow(item.id)}
                            disabled={extracting}
                        />
                    </Stack>
                );
            }
        }
    ];

    const activeItem = extractedItems.find((it) => it.id === expandedItemId);

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Scan Tally Sheet via AI (DeepSeek / Gemini)',
                subText: 'Unggah foto scan lembar Tally Sheet untuk mengekstrak barang & mengisi data Create Goods secara otomatis.'
            }}
            modalProps={{
                isBlocking: true,
                styles: { main: { maxWidth: '1000px !important', width: '92vw', maxHeight: '90vh' } }
            }}
        >
            <Stack tokens={{ childrenGap: 16 }} styles={{ root: { paddingTop: 6 } }}>
                {/* Upload & Model Selection Options */}
                <Stack
                    horizontal
                    wrap
                    tokens={{ childrenGap: 14 }}
                    verticalAlign="end"
                    styles={{
                        root: {
                            backgroundColor: '#f8fafc',
                            padding: '14px 16px',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0'
                        }
                    }}
                >
                    <Stack grow={1} tokens={{ childrenGap: 4 }}>
                        <Label size="small">Unggah Foto / Scan Tally Sheet (JPG, PNG, WEBP)</Label>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileChange(e.target.files)}
                            disabled={extracting}
                            style={{
                                padding: '6px 10px',
                                border: '1px dashed #94a3b8',
                                borderRadius: '6px',
                                backgroundColor: '#ffffff',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        />
                    </Stack>

                    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { width: 320 } }}>
                        <Label size="small">Model AI OCR</Label>
                        <Dropdown
                            selectedKey={selectedModel}
                            options={MODEL_OPTIONS}
                            onChange={(e, opt) => opt && setSelectedModel(opt.key as string)}
                            disabled={extracting}
                        />
                    </Stack>

                    <PrimaryButton
                        text={extracting ? 'Memproses...' : 'Scan via AI'}
                        iconProps={{ iconName: 'AutoEnhance' }}
                        disabled={!file || extracting || previewImages.length === 0}
                        onClick={handleProcessOCR}
                    />
                </Stack>

                {/* Error Banner */}
                {errorMessage && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                    </MessageBar>
                )}

                {/* Image Preview & Progress State */}
                {previewImages.length > 0 && (
                    <Stack horizontal tokens={{ childrenGap: 14 }} verticalAlign="start">
                        <Stack
                            styles={{
                                root: {
                                    width: extractedItems.length > 0 ? '200px' : '100%',
                                    maxHeight: '380px',
                                    overflowY: 'auto',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    backgroundColor: '#0f172a'
                                }
                            }}
                        >
                            {previewImages.map((imgSrc, idx) => (
                                <img
                                    key={idx}
                                    src={imgSrc}
                                    alt={`Tally Sheet Preview ${idx + 1}`}
                                    style={{ width: '100%', borderRadius: '4px', marginBottom: '8px' }}
                                />
                            ))}
                        </Stack>

                        {/* Extraction Loading Indicator */}
                        {extracting && (
                            <Stack grow={1} verticalAlign="center" horizontalAlign="center" styles={{ root: { padding: '40px 0' } }}>
                                <Spinner size={SpinnerSize.large} label="Menganalisis Tally Sheet via Google AI Studio Gemini (Free Tier)..." />
                                <Text size="small" style={{ color: '#0284c7', marginTop: 10, fontWeight: 500 }}>
                                    Ekstraksi Tally Sheet OCR 100% Gratis via Google AI Studio API
                                </Text>
                            </Stack>
                        )}

                        {/* Parsed Results Section */}
                        {!extracting && extractedItems.length > 0 && (
                            <Stack grow={1} tokens={{ childrenGap: 10 }} styles={{ root: { width: 'calc(100% - 214px)' } }}>
                                {/* Container Info Badge */}
                                {containerInfo && (containerInfo.containerNumber || containerInfo.merk || containerInfo.sealNumber) && (
                                    <Stack
                                        horizontal
                                        wrap
                                        tokens={{ childrenGap: 12 }}
                                        styles={{
                                            root: {
                                                backgroundColor: '#f1f5f9',
                                                border: '1px solid #cbd5e1',
                                                padding: '8px 12px',
                                                borderRadius: '6px'
                                            }
                                        }}
                                    >
                                        {containerInfo.containerNumber && (
                                            <Text size="small" style={{ fontWeight: 600, color: '#0f172a' }}>
                                                Container: {containerInfo.containerNumber}
                                            </Text>
                                        )}
                                        {containerInfo.sealNumber && (
                                            <Text size="small" style={{ color: '#475569' }}>
                                                Seal: {containerInfo.sealNumber}
                                            </Text>
                                        )}
                                        {containerInfo.merk && (
                                            <Text size="small" style={{ color: '#475569' }}>
                                                Merk: {containerInfo.merk}
                                            </Text>
                                        )}
                                    </Stack>
                                )}

                                {/* Results Header & Toolbar */}
                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                    <Text style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>
                                        Hasil Ekstraksi AI ({extractedItems.length} barang) — Klik 'Form' di setiap barang untuk mengisi detail Create Goods
                                    </Text>
                                    <DefaultButton
                                        text="Tambah Baris"
                                        iconProps={{ iconName: 'Plus' }}
                                        onClick={handleAddBlankRow}
                                    />
                                </Stack>

                                {/* Extracted Details List */}
                                <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                                    <ShimmeredDetailsList
                                        items={extractedItems}
                                        columns={columns}
                                        selectionMode={SelectionMode.none}
                                        enableShimmer={false}
                                    />
                                </div>

                                {/* Expanded Per-Item Create Goods Form Card */}
                                {activeItem && (
                                    <Stack
                                        tokens={{ childrenGap: 10 }}
                                        styles={{
                                            root: {
                                                backgroundColor: '#ffffff',
                                                border: '2px solid #0284c7',
                                                borderRadius: '8px',
                                                padding: '14px',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                                            }
                                        }}
                                    >
                                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                                <Icon iconName="Product" style={{ color: '#0284c7', fontSize: 16 }} />
                                                <Text style={{ fontWeight: 600, color: '#0369a1', fontSize: '14px' }}>
                                                    Form Create Goods Detail: {activeItem.jenisBarang || 'Barang Baru'}
                                                </Text>
                                            </Stack>
                                            <IconButton
                                                iconProps={{ iconName: 'Cancel' }}
                                                title="Tutup Form Detail"
                                                onClick={() => setExpandedItemId(undefined)}
                                            />
                                        </Stack>

                                        {/* Field Group 1: Company & Customer PIC */}
                                        <Stack horizontal tokens={{ childrenGap: 14 }}>
                                            <Stack.Item styles={{ root: { width: '50%' } }}>
                                                <Label required size="small">Owned By (Company)</Label>
                                                <TagPicker
                                                    selectedItems={activeItem.company ? [{ key: activeItem.company.id, name: activeItem.company.name, metadata: activeItem.company } as any] : []}
                                                    removeButtonAriaLabel='Remove'
                                                    itemLimit={1}
                                                    onItemSelected={(item: any) => {
                                                        const comp = item ? item.metadata : undefined;
                                                        handleItemChange(activeItem.id, 'company', comp);
                                                        handleItemChange(activeItem.id, 'customer', undefined);
                                                        return null;
                                                    }}
                                                    onChange={(items) => {
                                                        if (!items || items.length < 1) {
                                                            handleItemChange(activeItem.id, 'company', undefined);
                                                            handleItemChange(activeItem.id, 'customer', undefined);
                                                        }
                                                    }}
                                                    onResolveSuggestions={_onSearchCompanies}
                                                    onEmptyResolveSuggestions={() => _onSearchCompanies('')}
                                                />
                                            </Stack.Item>

                                            <Stack.Item styles={{ root: { width: '50%' } }}>
                                                <Label required size="small">Customer (PIC)</Label>
                                                <NormalPeoplePicker
                                                    onResolveSuggestions={(filter) => _onSearchCustomers(activeItem.company?.id, filter)}
                                                    onEmptyResolveSuggestions={() => _onSearchCustomers(activeItem.company?.id, '')}
                                                    itemLimit={1}
                                                    selectedItems={activeItem.customer ? [{
                                                        id: activeItem.customer.id,
                                                        text: activeItem.customer.name,
                                                        secondaryText: activeItem.customer.email,
                                                        imageInitials: GeneralService.getInitial(activeItem.customer.name),
                                                        metadata: activeItem.customer,
                                                    } as any] : []}
                                                    disabled={!activeItem.company}
                                                    onChange={(item?: any) => {
                                                        handleItemChange(activeItem.id, 'customer', item && item[0] ? item[0].metadata : undefined);
                                                        return null;
                                                    }}
                                                />
                                            </Stack.Item>
                                        </Stack>

                                        {/* Field Group 2: Dimensions & Volume */}
                                        <Stack tokens={{ childrenGap: 6 }} styles={{ root: { backgroundColor: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' } }}>
                                            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                                <Label size="small" style={{ fontWeight: 600 }}>Dimensions & Volume</Label>
                                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                                                    <Label size="small">Satuan Dimensi:</Label>
                                                    <Dropdown
                                                        options={DIMENSION_UNITS}
                                                        selectedKey={activeItem.dimensionUnit || 'cm'}
                                                        onChange={(e, opt) => opt && handleItemChange(activeItem.id, 'dimensionUnit', opt.key)}
                                                        styles={{ root: { width: 80 } }}
                                                    />
                                                </Stack>
                                            </Stack>

                                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                                <Stack.Item styles={{ root: { width: '25%' } }}>
                                                    <TextField
                                                        label="Length"
                                                        value={activeItem.length || ''}
                                                        suffix={activeItem.dimensionUnit || 'cm'}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'length', val || '')}
                                                        placeholder="0"
                                                    />
                                                </Stack.Item>
                                                <Stack.Item styles={{ root: { width: '25%' } }}>
                                                    <TextField
                                                        label="Width"
                                                        value={activeItem.width || ''}
                                                        suffix={activeItem.dimensionUnit || 'cm'}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'width', val || '')}
                                                        placeholder="0"
                                                    />
                                                </Stack.Item>
                                                <Stack.Item styles={{ root: { width: '25%' } }}>
                                                    <TextField
                                                        label="Height"
                                                        value={activeItem.height || ''}
                                                        suffix={activeItem.dimensionUnit || 'cm'}
                                                        onChange={(e, val) => handleItemChange(activeItem.id, 'height', val || '')}
                                                        placeholder="0"
                                                    />
                                                </Stack.Item>
                                                <Stack.Item styles={{ root: { width: '25%' } }}>
                                                    <Label size="small">Volume (m3)</Label>
                                                    <div style={{ height: 32, display: 'flex', alignItems: 'center', fontWeight: 600, color: '#0369a1' }}>
                                                        {GeneralService.getNumberWithSeparator(GeneralService.calculateVolume(activeItem.length, activeItem.width, activeItem.height, activeItem.dimensionUnit || 'cm'))} m3
                                                    </div>
                                                </Stack.Item>
                                            </Stack>
                                        </Stack>

                                        {/* Field Group 3: Warehouse Location & Location Details */}
                                        <Stack horizontal tokens={{ childrenGap: 14 }}>
                                            <Stack.Item styles={{ root: { width: '50%' } }}>
                                                <Label size="small">Warehouse Location</Label>
                                                <TagPicker
                                                    selectedItems={activeItem.warehouse ? [{ key: activeItem.warehouse.id, name: activeItem.warehouse.name, metadata: activeItem.warehouse } as any] : []}
                                                    removeButtonAriaLabel='Remove'
                                                    itemLimit={1}
                                                    onItemSelected={(item: any) => {
                                                        handleItemChange(activeItem.id, 'warehouse', item ? item.metadata : undefined);
                                                        return null;
                                                    }}
                                                    onChange={(items) => {
                                                        if (!items || items.length < 1) handleItemChange(activeItem.id, 'warehouse', undefined);
                                                    }}
                                                    onResolveSuggestions={_onSearchWarehouse}
                                                    onEmptyResolveSuggestions={() => _onSearchWarehouse('')}
                                                />
                                            </Stack.Item>

                                            <Stack.Item styles={{ root: { width: '50%' } }}>
                                                <Label size="small">Location Details</Label>
                                                <TextField
                                                    value={activeItem.locationDetails || ''}
                                                    onChange={(e, val) => handleItemChange(activeItem.id, 'locationDetails', val || '')}
                                                    placeholder="Detail Rak / Baris Gudang"
                                                />
                                            </Stack.Item>
                                        </Stack>

                                        {/* Field Group 4: Supplier, Delivery Date & Notes */}
                                        <Stack horizontal tokens={{ childrenGap: 14 }}>
                                            <Stack.Item styles={{ root: { width: '33%' } }}>
                                                <Label size="small">Supplier</Label>
                                                <TextField
                                                    value={activeItem.supplier || ''}
                                                    onChange={(e, val) => handleItemChange(activeItem.id, 'supplier', val || '')}
                                                    placeholder="Nama Supplier"
                                                />
                                            </Stack.Item>

                                            <Stack.Item styles={{ root: { width: '33%' } }}>
                                                <Label size="small">Delivery Date</Label>
                                                <DatePicker
                                                    value={activeItem.deliveryDate ? moment(activeItem.deliveryDate).toDate() : undefined}
                                                    onSelectDate={(date) => handleItemChange(activeItem.id, 'deliveryDate', date ? moment(date).toISOString() : undefined)}
                                                    formatDate={GeneralService.formatDate}
                                                />
                                            </Stack.Item>

                                            <Stack.Item styles={{ root: { width: '34%' } }}>
                                                <Label size="small">Notes / Catatan</Label>
                                                <TextField
                                                    value={activeItem.notes || ''}
                                                    onChange={(e, val) => handleItemChange(activeItem.id, 'notes', val || '')}
                                                    placeholder="Catatan tambahan"
                                                />
                                            </Stack.Item>
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Stack>
                )}

                {/* Footer Action Buttons */}
                <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }} styles={{ root: { paddingTop: 8 } }}>
                    <DefaultButton text="Batal" onClick={onDismiss} disabled={extracting} />
                    {extractedItems.length > 0 && (
                        <PrimaryButton
                            text={`Terapkan ${extractedItems.length} Barang ke Packing List`}
                            iconProps={{ iconName: 'CheckMark' }}
                            onClick={handleApply}
                            disabled={extracting}
                        />
                    )}
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default TallySheetScanModal;
